import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { apiBaseUrl } from '@config/environment';
import { auth } from '@config/firebase';
import { cache } from '@utils/cache';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      // Avoid mutating the interceptor param directly to satisfy no-param-reassign
      const newConfig: InternalAxiosRequestConfig = { ...config };
      // Use a loose cast here to satisfy Axios' header type (AxiosRequestHeaders has methods).
      const headers = {
        ...(newConfig.headers as Record<string, unknown>),
        Authorization: `Bearer ${token}`,
      } as any;
      newConfig.headers = headers;
      return newConfig;
    }

    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async error => {
    const apiMessage = error?.response?.data?.message;
    const normalizedError =
      typeof apiMessage === 'string' && apiMessage.trim()
        ? Object.assign(new Error(apiMessage), error)
        : error;

    if (normalizedError.response?.status === 401) {
      // Token is invalid or revoked (e.g. password changed on another device).
      // Force-refresh to give Firebase a chance to obtain a new token; if the
      // refresh token was revoked, this will throw and Firebase will sign out.
      const user = auth.currentUser;
      if (user) {
        try {
          await user.getIdToken(true);
        } catch {
          // Refresh failed — session revoked. Sign out to redirect to login.
          console.error('[api] 401 — refresh token revoked. Signing out.');
          await auth.signOut();
        }
      }
    }
    return Promise.reject(normalizedError);
  }
);

// Generic API methods
const api = {
  // Helper to normalize API responses: unwrap { success, data } when present
  // If API returns { success: false, message }, throw an Error to surface it clearly
  // Otherwise return raw payload
  // Note: keeps backward compatibility with endpoints that already return raw objects
  get: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig & { cache?: boolean; cacheTTL?: number; raw?: boolean }
  ): Promise<T> => {
    const unwrap = (payload: unknown, skipUnwrap: boolean) => {
      if (skipUnwrap) return payload as T;
      if (payload && typeof payload === 'object') {
        const p = payload as Record<string, unknown>;
        if (p.success === true && 'data' in p) return p.data as T;
        if (p.success === false) {
          const err = new Error(String(p.message || 'API error')) as Error & { response?: unknown };
          err.response = { data: payload };
          throw err;
        }
      }
      return payload as T;
    };

    const { cache: useCache = false, cacheTTL, raw = false, ...axiosConfig } = config || {};
    const cacheKey = `api:get:${url}:${JSON.stringify(axiosConfig.params || {})}`;

    if (useCache) {
      return cache.getOrFetch(
        cacheKey,
        () => apiClient.get(url, axiosConfig).then(res => unwrap(res.data, raw)),
        cacheTTL
      );
    }

    return apiClient.get(url, axiosConfig).then(res => unwrap(res.data, raw));
  },

  getWithHeaders: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig & { raw?: boolean }
  ): Promise<{ data: T; headers: Record<string, unknown> }> => {
    const unwrap = (payload: unknown, skipUnwrap: boolean) => {
      if (skipUnwrap) return payload as T;
      if (payload && typeof payload === 'object') {
        const p = payload as Record<string, unknown>;
        if (p.success === true && 'data' in p) return p.data as T;
        if (p.success === false) {
          const err = new Error(String(p.message || 'API error')) as Error & { response?: unknown };
          err.response = { data: payload };
          throw err;
        }
      }
      return payload as T;
    };

    const { raw = false, ...axiosConfig } = config || {};
    return apiClient.get(url, axiosConfig).then(res => ({
      data: unwrap(res.data, raw),
      headers: res.headers as Record<string, unknown>,
    }));
  },

  post: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig & { raw?: boolean }
  ): Promise<T> => {
    // Invalidate related cache on mutations
    cache.invalidatePattern(`api:get:${url.split('?')[0]}`);
    return apiClient.post(url, data, config).then(res => {
      const payload = res.data;
      if (payload && typeof payload === 'object' && (payload as any).success === false) {
        const err = new Error(String((payload as any).message || 'API error')) as Error & {
          response?: unknown;
        };
        err.response = { data: payload };
        throw err;
      }
      // If caller asked for raw payload, return the full payload (useful for top-level flags)
      if (config && (config as any).raw) return payload as unknown as T;
      return payload && (payload as any).success === true && 'data' in payload
        ? (payload as any).data
        : payload;
    });
  },

  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    // Invalidate related cache on mutations
    cache.invalidatePattern(`api:get:${url.split('/').slice(0, -1).join('/')}`);
    return apiClient.put(url, data, config).then(res => {
      const payload = res.data;
      if (payload && typeof payload === 'object' && (payload as any).success === false) {
        const err = new Error(String((payload as any).message || 'API error')) as Error & {
          response?: unknown;
        };
        err.response = { data: payload };
        throw err;
      }
      return payload && (payload as any).success === true && 'data' in payload
        ? (payload as any).data
        : payload;
    });
  },

  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    // Invalidate related cache on mutations
    cache.invalidatePattern(`api:get:${url.split('/').slice(0, -1).join('/')}`);
    return apiClient.patch(url, data, config).then(res => {
      const payload = res.data;
      if (payload && typeof payload === 'object' && (payload as any).success === false) {
        const err = new Error(String((payload as any).message || 'API error')) as Error & {
          response?: unknown;
        };
        err.response = { data: payload };
        throw err;
      }
      return payload && (payload as any).success === true && 'data' in payload
        ? (payload as any).data
        : payload;
    });
  },

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    // Invalidate related cache on mutations
    cache.invalidatePattern(`api:get:${url.split('/').slice(0, -1).join('/')}`);
    return apiClient.delete(url, config).then(res => {
      const payload = res.data;
      if (payload && typeof payload === 'object' && (payload as any).success === false) {
        const err = new Error(String((payload as any).message || 'API error')) as Error & {
          response?: unknown;
        };
        err.response = { data: payload };
        throw err;
      }
      return payload && (payload as any).success === true && 'data' in payload
        ? (payload as any).data
        : payload;
    });
  },
};

export default api;
